import { createContext, ReactNode, useContext, useMemo } from 'react';
import {
  useSuiteActions,
  UseSuiteActionsReturn,
} from 'src/SharedCustomHooks/UseSuiteActions/useSuiteActions';

interface ContactsDataType {
  isLoading: boolean;
  isFormLoading: boolean;
  error: string | null;
}

interface ContactsActionsType {
  setLoading: (loading: boolean) => void;
  setFormLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearState: () => void;
  fetchBuildingForProperty: (propertyId: string) => Promise<any[]>;
  fetchLevelsForBuilding: (buildingId: any) => Promise<any>;
  fetchSuiteById: (suiteId: string) => Promise<any | null>;
  fetchSuiteCounts: (suiteId: string, role: string) => Promise<any | null>;
  fetchPropertySuites: (
    propertyId: string,
    userId: string,
    role: string
  ) => Promise<any[]>;
  fetchProperties: (userId: string, role: string) => Promise<any[]>;
  fetchAssetsBySuiteId: (
    suiteId: string,
    userId: string,
    role: string
  ) => Promise<any[]>;
  fetchSuiteWalkThroughs: (
    suiteId: string,
    roleName: string,
    flag?: boolean
  ) => Promise<any[]>;
  fetchFaqsNotesDocuments: (suiteId: string) => Promise<any[]>;
  fetchPropertyManagerDetailsForHomeowner: (
    propertyId: string
  ) => Promise<any | null>;
  fetchCalendlyLink: (email: string) => Promise<any | null>;
  fetchAllowedTabList: (propertyId: string, branding: any) => Promise<any[]>;
  getAllUserTypeRoles: () => Promise<any[]>;
  updateSuite: (suiteData: any) => Promise<any | null>;
  deleteUnit: (
    unitId: string,
    userId: string,
    history: any,
    property: any
  ) => Promise<boolean>;
  fetchMoreDetails: (suiteId: string) => Promise<any | null>;
  reRenderKeyPickupPdf: (suiteId: string) => Promise<boolean>;
}

interface ContactsProviderProps {
  children: ReactNode;
}

const DataContext = createContext<ContactsDataType | undefined>(undefined);
const ActionsContext = createContext<ContactsActionsType | undefined>(
  undefined
);

export const ContactsSuiteActionsProvider = ({
  children,
}: ContactsProviderProps) => {
  const suiteActions: UseSuiteActionsReturn = useSuiteActions();

  const dataValue = useMemo(
    () => ({
      isLoading: suiteActions.isLoading,
      isFormLoading: suiteActions.isFormLoading,
      error: suiteActions.error,
    }),
    [suiteActions.isLoading, suiteActions.isFormLoading, suiteActions.error]
  );

  const actionsValue = useMemo(
    () => ({
      setLoading: suiteActions.setLoading,
      setFormLoading: suiteActions.setFormLoading,
      setError: suiteActions.setError,
      clearState: suiteActions.clearState,
      fetchBuildingForProperty: suiteActions.fetchBuildingForProperty,
      fetchLevelsForBuilding: suiteActions.fetchLevelsForBuilding,
      fetchSuiteById: suiteActions.fetchSuiteById,
      fetchSuiteCounts: suiteActions.fetchSuiteCounts,
      fetchPropertySuites: suiteActions.fetchPropertySuites,
      fetchProperties: suiteActions.fetchProperties,
      fetchAssetsBySuiteId: suiteActions.fetchAssetsBySuiteId,
      fetchSuiteWalkThroughs: suiteActions.fetchSuiteWalkThroughs,
      fetchFaqsNotesDocuments: suiteActions.fetchFaqsNotesDocuments,
      fetchPropertyManagerDetailsForHomeowner:
        suiteActions.fetchPropertyManagerDetailsForHomeowner,
      fetchCalendlyLink: suiteActions.fetchCalendlyLink,
      fetchAllowedTabList: suiteActions.fetchAllowedTabList,
      getAllUserTypeRoles: suiteActions.getAllUserTypeRoles,
      updateSuite: suiteActions.updateSuite,
      deleteUnit: suiteActions.deleteUnit,
      fetchMoreDetails: suiteActions.fetchMoreDetails,
      reRenderKeyPickupPdf: suiteActions.reRenderKeyPickupPdf,
    }),
    [
      suiteActions.setLoading,
      suiteActions.setFormLoading,
      suiteActions.setError,
      suiteActions.clearState,
      suiteActions.fetchBuildingForProperty,
      suiteActions.fetchLevelsForBuilding,
      suiteActions.fetchSuiteById,
      suiteActions.fetchSuiteCounts,
      suiteActions.fetchPropertySuites,
      suiteActions.fetchProperties,
      suiteActions.fetchAssetsBySuiteId,
      suiteActions.fetchSuiteWalkThroughs,
      suiteActions.fetchFaqsNotesDocuments,
      suiteActions.fetchPropertyManagerDetailsForHomeowner,
      suiteActions.fetchCalendlyLink,
      suiteActions.fetchAllowedTabList,
      suiteActions.getAllUserTypeRoles,
      suiteActions.updateSuite,
      suiteActions.deleteUnit,
      suiteActions.fetchMoreDetails,
      suiteActions.reRenderKeyPickupPdf,
    ]
  );

  return (
    <ActionsContext.Provider value={actionsValue}>
      <DataContext.Provider value={dataValue}>{children}</DataContext.Provider>
    </ActionsContext.Provider>
  );
};

export const useContactsSuiteActionsDataFromContext = (): ContactsDataType => {
  const context: ContactsDataType = useContext(DataContext);

  if (context === undefined) {
    throw new Error(
      'useContactsDataFromContext must be used within a ContactsProvider'
    );
  }

  return context;
};

export const useContactsSuiteActionsFromContext = (): ContactsActionsType => {
  const context: ContactsActionsType = useContext(ActionsContext);

  if (context === undefined) {
    throw new Error(
      'useContactsActionsFromContext must be used within a ContactsProvider'
    );
  }

  return context;
};
